import { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import { motion } from 'framer-motion';

const PortfolioPerformance = ({ portfolioData = [], performanceData = [], userData }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!performanceData || performanceData.length === 0) {
      return;
    }

    try {
      // The dashboard uses a matte-dark stone theme, so we configure the chart to match.
      const chartOptions = {
        layout: {
          background: { color: 'transparent' },
          textColor: 'rgba(231, 229, 228, 0.9)',
          fontSize: 12,
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { color: 'rgba(255, 255, 255, 0.06)' },
        },
        width: chartContainerRef.current.clientWidth,
        height: 200,
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: 'rgba(255, 255, 255, 0.12)',
        },
        rightPriceScale: {
          borderColor: 'rgba(255, 255, 255, 0.12)',
        },
        crosshair: {
          vertLine: {
            color: 'rgba(34, 211, 238, 0.18)',
            width: 1,
            style: 1,
          },
          horzLine: {
            color: 'rgba(34, 211, 238, 0.18)',
            width: 1,
            style: 1,
          },
        },
      };

      chartRef.current = createChart(chartContainerRef.current, chartOptions);
      const areaSeries = chartRef.current.addAreaSeries({
        topColor: 'rgba(34, 211, 238, 0.22)',
        bottomColor: 'rgba(34, 211, 238, 0.0)',
        lineColor: 'rgba(34, 211, 238, 1)',
        lineWidth: 2,
      });

      const chartData = performanceData.map(item => ({
        time: item.month,
        value: item.value,
      }));

      areaSeries.setData(chartData);
      chartRef.current.timeScale().fitContent();

      const handleResize = () => {
        if (chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
        }
      };
    } catch (error) {
      console.error('Chart error:', error);
    }
  }, [performanceData]);

  // Calculate performance metrics based on real investment data
  const calculateMetrics = () => {
    if (!userData?.investments || userData.investments.length === 0) {
      return {
        totalReturn: 0,
        monthlyGrowth: 0,
        annualizedReturn: 0,
        activeInvestments: 0,
        totalInvestments: 0
      };
    }

    const totalInvested = userData.totalInvested || 0;
    const totalProfit = userData.totalProfit || 0;
    
    const totalReturn = totalInvested > 0 ? ((totalProfit / totalInvested) * 100) : 0;
    const activeInvestments = userData.investments.filter(inv => inv.status === 'active').length;
    const totalInvestments = userData.investments.length;
    
    // Calculate average monthly growth based on investment duration
    const avgInvestmentDuration = 21; // 21 days per investment
    const monthlyGrowth = totalReturn / (avgInvestmentDuration / 30);
    const annualizedReturn = monthlyGrowth * 12;

    return {
      totalReturn: totalReturn.toFixed(2),
      monthlyGrowth: monthlyGrowth.toFixed(2),
      annualizedReturn: annualizedReturn.toFixed(2),
      activeInvestments,
      totalInvestments
    };
  };

  const metrics = calculateMetrics();

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card-glass bg-stone-950/20 rounded-xl p-3 border border-white/10">
          <h3 className="text-xs font-medium text-stone-300">Total Balance</h3>
          <p className="text-base font-semibold mt-1 text-white">
            ${userData?.balance?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="card-glass bg-stone-950/20 rounded-xl p-3 border border-white/10">
          <h3 className="text-xs font-medium text-stone-300">Total Invested</h3>
          <p className="text-base font-semibold mt-1 text-white">
            ${userData?.totalInvested?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="card-glass bg-stone-950/20 rounded-xl p-3 border border-white/10">
          <h3 className="text-xs font-medium text-stone-300">Total Profit</h3>
          <p className="text-base font-semibold mt-1 text-white">
            ${userData?.totalProfit?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="card-glass bg-stone-950/20 rounded-xl p-3 border border-white/10">
          <h3 className="text-xs font-medium text-stone-300">Return Rate</h3>
          <p className={`text-base font-bold mt-1 ${
            parseFloat(metrics.totalReturn) >= 0 ? 'text-emerald-300' : 'text-rose-300'
          }`}>
            {metrics.totalReturn}%
          </p>
        </div>
      </div>

      {/* Performance Chart */}
      {performanceData.length > 0 ? (
        <div className="card-glass bg-stone-950/20 rounded-xl p-3 border border-white/10">
          <div className="text-sm font-medium text-cyan-200 mb-2">Investment Timeline</div>
          <div ref={chartContainerRef} className="w-full" />
        </div>
      ) : (
        <div className="card-glass bg-stone-950/20 rounded-xl p-6 text-center border border-white/10">
          <div className="text-stone-300 text-sm">No investment data available</div>
          <div className="text-stone-500 text-xs mt-1">Start investing to see your performance chart</div>
        </div>
      )}

      {/* Portfolio Distribution */}
      <div className="grid grid-cols-2 gap-3">
        {portfolioData?.map((item, index) => (
          <div key={index} className="card-glass bg-stone-950/20 rounded-xl p-3 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-stone-300">{item.name}</span>
              <span className="text-xs font-semibold text-white">{item.value}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 to-sky-400"
                style={{ width: `${item.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Investment Summary */}
      <div className="card-glass bg-stone-950/20 rounded-xl p-4 border border-white/10">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-white">{metrics.activeInvestments}</div>
            <div className="text-xs text-stone-400">Active Investments</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">{metrics.totalInvestments}</div>
            <div className="text-xs text-stone-400">Total Investments</div>
          </div>
          <div className="col-span-2 md:col-span-1">
            <div className="text-lg font-semibold text-cyan-200">{metrics.annualizedReturn}%</div>
            <div className="text-xs text-stone-400">Annualized Return</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PortfolioPerformance; 